
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CustomCircularProgressBar
 *
 * @file CustomCircularProgressBar.jsx
 * @author Mohamed yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import mixins from '../../assets/styles/mixins.scss';
import HtmlTooltip from './HtmlTooltip';
import ToolTipContent from './ToolTipContent';
import { getBackgroundColor } from '../../common/statusColorUtils';
import Utils from '../../common/utils';
import { COMPLETED } from '../../common/constants';

function polarToCartesian(cx, cy, r, angle) {
  const rad = ((angle - 90) * Math.PI) / 180.0;

  return {
    x: cx + (r * Math.cos(rad)),
    y: cy + (r * Math.sin(rad))
  };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  return [
    'M', start.x, start.y,
    'A', r, r, 0, largeArcFlag, 0, end.x, end.y
  ].join(' ');
}

const SvgContainer = styled('div')({
  '&&': {
    position: 'relative',
    maxWidth: 130,
    maxHeight: 130,
    padding: 4
  }
});

const CenterLabel = styled('div')({
  '&&': {
    position: 'absolute',
    top: '50%',
    left: '52%',
    transform: 'translate(-50%, -48%)',
    textAlign: 'center',
    userSelect: 'none'
  }
});

const CenterLabelLarge = styled(Typography)({
  '&&': {
    color: mixins.color_text_primary,
    textAlign: 'center',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '32px',
    letterSpacing: '-0.64px'
  }
});

const CenterLabelSub = styled(Typography)({
  '&&': {
    color: mixins.zambezi,
    textAlign: 'center',
    fontFeatureSettings: '\'liga\' off',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize10,
    fontStyle: 'normal',
    fontWeight: mixins.fontWeight600,
    lineHeight: '12px'
  }
});

const Dot = styled('div')({
  '&&': {
    width: 6,
    height: 6,
    borderRadius: '50%',
    display: 'inline-block'
  }
});

const DotContainerLeft = styled('div')({
  '&&': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4
  }
});

const DotContainer = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  }
});

const CircularProgressBarWidget = ({
  segments,
  total,
  progressLabel,
  progressSubLabel,
  width,
  height,
  strokeWidth,
  gap,
  onMouseEnter,
  onMouseLeave,
  activeColor,
  strokeColor,
  unControlledTooltip,
  enableDotIndicators
}) => {
  const cx = width / 2;
  const cy = width / 2;
  const r = width / 2 - strokeWidth / 2;
  const nonZeroSegments = segments.filter(seg => seg.valueInPercentage > 0);
  const totalGap = gap && nonZeroSegments.length > 1 ? nonZeroSegments.length * strokeWidth : 0;

  let startAngle = 0;

  return (
    <SvgContainer>
      <svg width={width} height={height} style={{ overflow: 'visible' }}>
        {/* Background circle */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
        />
        {/* Segments */}
        {segments.map((seg, index) => {
          if (seg.valueInPercentage === 0) return null;
          const segmentAngle = ((seg.valueInPercentage / total) * (360 - totalGap));
          const segStart = startAngle + (gap && nonZeroSegments.length > 1 ? strokeWidth : 0);
          const segEnd = segStart + segmentAngle;

          // For full circle (or nearly full), use circle element instead of arc
          const isFullCircle = segmentAngle >= 359.9;

          startAngle = segEnd;
          const active = activeColor === seg.color;
          const notSameStrokeColor = !(seg.color === strokeColor);
          const extraProps = unControlledTooltip ? { open: active } : {};

          return (
            <g key={seg.label}>
              <HtmlTooltip title={<ToolTipContent text={`${seg.label}: ${seg.value}`} />} arrow placement="top" {...extraProps}>
                {isFullCircle ? (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={r}
                    fill="none"
                    stroke={seg.color}
                    strokeWidth={(notSameStrokeColor && active) ? strokeWidth + 4 : strokeWidth}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (notSameStrokeColor) {
                        event.target.setAttribute('stroke-width', strokeWidth + 4);
                      }
                      onMouseEnter(seg);
                    }}
                    onMouseLeave={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      event.target.setAttribute('stroke-width', strokeWidth);
                      onMouseLeave();
                    }}
                  />
                ) : (
                  <path
                    d={describeArc(cx, cy, r, segStart, segEnd)}
                    stroke={seg.color}
                    strokeWidth={(notSameStrokeColor && active) ? strokeWidth + 4 : strokeWidth}
                    strokeLinecap={index === segments.length - 1 ? 'inherit' : 'round'}
                    fill="none"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (notSameStrokeColor) {
                        event.target.setAttribute('stroke-width', strokeWidth + 4);
                      }
                      onMouseEnter(seg);
                    }}
                    onMouseLeave={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      event.target.setAttribute('stroke-width', strokeWidth);
                      onMouseLeave();
                    }}
                  />
                )}
              </HtmlTooltip>
            </g>
          );
        })}
      </svg>
      {(progressLabel || progressLabel === 0) ?
        (
          <CenterLabel>
            <CenterLabelLarge>{progressLabel}</CenterLabelLarge>
            <DotContainer>

              {/* Dot indicators */}
              {enableDotIndicators && (
                <DotContainerLeft>
                  {segments.filter(seg => Utils.localStringCompare(seg.type, COMPLETED)).map((seg) => (
                    <Dot key={seg.label} style={{ backgroundColor: seg.color }} />
                  ))}
                </DotContainerLeft>
              )}

              {progressSubLabel ? <CenterLabelSub>{progressSubLabel}</CenterLabelSub> : null}
            </DotContainer>
          </CenterLabel>
        ) : null}
    </SvgContainer>
  );
};

CircularProgressBarWidget.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      valueInPercentage: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  total: PropTypes.number,
  progressLabel: PropTypes.string,
  progressSubLabel: PropTypes.string,
  width: PropTypes.number,
  height: PropTypes.number,
  strokeWidth: PropTypes.number,
  gap: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  activeColor: PropTypes.string,
  strokeColor: PropTypes.string,
  unControlledTooltip: PropTypes.bool,
  enableDotIndicators: PropTypes.bool
};

CircularProgressBarWidget.defaultProps = {
  total: 100,
  width: 128,
  height: 128,
  strokeWidth: 12,
  gap: true,
  progressLabel: null,
  progressSubLabel: null,
  onMouseEnter: () => {},
  onMouseLeave: () => {},
  activeColor: null,
  strokeColor: getBackgroundColor('notAttempted'),
  unControlledTooltip: false,
  enableDotIndicators: false
};

export default CircularProgressBarWidget;
